/// <amd-module name="Pipeline/Analytics/PipelineLeadTimeCardMetrics/PipelineLeadTime.CardMetrics" />
import * as PipelineAnalytics from "Analytics/PipelineAnalytics/PipelineAnalyticsClient/Contracts/PipelineAnalytics";
import {
    getPipelineAnalyticsClient,
    IPipelineAnalyticsRestClient
} from "Analytics/PipelineAnalytics/PipelineAnalyticsClient/RestClient/PipelineAnalytics";
import { CardMetricsVisual } from "Analytics/Shared/Visuals/CardMetricsVisual/CardMetricsVisual";
import { IPipelineAnalyticsTelemetryService } from "Pipeline/Analytics/Common/Telemetry/PipelineAnalyticsTelemetryService";
import * as TelemetryConstants from "Pipeline/Analytics/Common/Telemetry/TelemetryConstants";
import * as Resources from "Pipeline/Analytics/PipelineLeadTimeCardMetrics/Resources";
import * as React from "react";
import { getTFSPageData } from "Tfs/Platform/Page";
import * as Utils_String from "VSS/Core/Util/String";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { Components, IVssComponentContext, IVssComponentProperties, VssComponent } from "VSS/Platform/Layout";
import { ISettingsService, SettingsUserScope } from "VSS/Platform/Settings";

export interface ICardMetricsProps extends IVssComponentProperties {
    definitionId: number;
    onCardClick: (routeId: string, event?: any) => void;
    contextType: string;
    errorText?: string;
}

interface LeadTimeEnabledDefinitionIds {
    projectId: string;
    definitionIds: number[];
}

interface IDictionaryNumberTo<T> {
    [key: number]: T;
}

export interface IPipelineLeadTimeCardMetricsState {
    stageName: string;
    leadTimeInDays: string;
    loading: boolean;
}

export class PipelineLeadTimeCardMetrics extends VssComponent<ICardMetricsProps, IPipelineLeadTimeCardMetricsState> {
    private static readonly _pipelineLeadTimeHubRouteUrl: string = "ms.vss-pipelineanalytics-web.pipeline-leadtime-route";
    private static readonly _registryPath: string = "Pipeline/leadtime/definitionIds";
    private static readonly _daysRegistryPath: string = "Pipeline/leadtime/agoDays";
    private static readonly _percentileRegistryPath: string = "Pipeline/leadtime/percentile";
    private static readonly _forwardLink = "https://go.microsoft.com/fwlink/?linkid=2094586";
    private static readonly _forwardLinkForNotOptedIn =
        "https://go.microsoft.com/fwlink/?linkid=2094586#How_to_enable_Lead_time_report_for_your_release";
    private static readonly _metricBubblingFeatureFlag = "Pipeline.LeadTimeMetrics.BubbleLeadTimeMetricsOnCard";
    private _projectId: string;
    private _agoDays: number;
    private _percentile: number;
    private _pipelineAnalyticsPromise: Promise<PipelineAnalytics.StageLeadTime[]>;
    private _pipelineAnalyticsClient: IPipelineAnalyticsRestClient;

    constructor(props: ICardMetricsProps, context: IVssComponentContext) {
        super(props, context);
        const tfsPageData = getTFSPageData(context.pageContext);
        this._projectId = tfsPageData!.project!.id;

        this._pipelineAnalyticsClient = getPipelineAnalyticsClient(context.pageContext);
        this._refreshLeadMetrics(this.props.definitionId);
    }

    public componentDidUpdate(prevProps: ICardMetricsProps, prevState: IPipelineLeadTimeCardMetricsState): void {
        if (this.props.definitionId !== prevProps.definitionId) {
            this._refreshLeadMetrics(this.props.definitionId);
        }

        super.componentDidUpdate(prevProps, prevState);
    }

    public render() {
        const isReportOptedIn = this._isReportOptedIn(this.props.definitionId);
        const linkTarget = isReportOptedIn ? PipelineLeadTimeCardMetrics._forwardLink : undefined;
        const shouldBubbleMetrics = this._isFeatureEnabled() && isReportOptedIn;
        const reportInfo = isReportOptedIn ? (shouldBubbleMetrics ? undefined : Resources.ViewOptedInReport) : Resources.ViewOptedOutReport;
        const secondaryMetrics = this._getSecondaryMetrics();
        const subTitle = Utils_String.format(Resources.LeadTimeCardSubtitle, this._getPercentileToCalculateLeadTime(), this._getNumberOfDaysToCalculateLeadTime());

        return (
            <CardMetricsVisual
                title={Resources.PipelineLeadTime}
                subtitle={shouldBubbleMetrics ? subTitle : undefined}
                dataValidationPromises={[this._pipelineAnalyticsPromise]}
                primaryMetric={undefined}
                secondaryMetric={shouldBubbleMetrics ? secondaryMetrics : undefined}
                insight={""}
                onCardClick={this._onCardClick}
                info={reportInfo}
                linkTarget={linkTarget}
                onLinkClick={this._onLinkClick}
            />
        );
    }

    private _getSecondaryMetrics(){
        if(this._isLoadingComplete()){
            let value = Utils_String.format(Resources.LeadTimeDays, this.state.leadTimeInDays);
            let footer = this.state.stageName;

            return { value: value, footer: footer };
        }

        return undefined;
    }

    private async _refreshLeadMetrics(definitionId: number): Promise<void> {
        if (!this._isFeatureEnabled()) {
            return;
        }

        let cacheMetrics = this._cachedLeadTimeMetrics[definitionId]
        if (cacheMetrics) {
            this.setState({ stageName: cacheMetrics.stageName, leadTimeInDays: cacheMetrics.leadTime, loading: false });
        }
        else {
            this.setState({ loading: true });
        }

        const numberOfDays = this._getNumberOfDaysToCalculateLeadTime();
        const percentile = this._getPercentileToCalculateLeadTime();
        const leadTime = await this._pipelineAnalyticsClient.getLeadTime(this._projectId, definitionId, 1, numberOfDays, percentile);
        // If callback comes from definition which is currently selected then only refresh the state.
        var isCurrentDefinition = this.props.definitionId === definitionId;
        if (isCurrentDefinition && leadTime && leadTime.length > 0) {
            const metrics = leadTime[0];

            this._setLeadTimeMetricDataCache(definitionId, metrics);
            this.setState({ stageName: metrics.stageName, leadTimeInDays: metrics.leadTime, loading: false });
        }
    }

    private _isFeatureEnabled() {
         return isFeatureFlagEnabled(this.context.pageContext, PipelineLeadTimeCardMetrics._metricBubblingFeatureFlag, false);
    }

    private _onLinkClick = (event?: any): void => {
        const telemetryService = this.context.pageContext.getService<IPipelineAnalyticsTelemetryService>("IPipelineAnalyticsTelemetryService");
        telemetryService.publishEventWithSingleKey(
            TelemetryConstants.feature_PipelineAnalyticsLeadTimeCard,
            TelemetryConstants.LearnMoreClickCount,
            1
        );
    };

    private _onCardClick = (event?: any): void => {
        if (!this._isReportOptedIn(this.props.definitionId)) {
            this._publishPipelineCardClickTelemetry(TelemetryConstants.feature_PipelineAnalyticsLeadTimeCardNotOptedIn);
            window.open(PipelineLeadTimeCardMetrics._forwardLinkForNotOptedIn, "_blank");
            return;
        }

        if (this.props.onCardClick) {
            const routeId = PipelineLeadTimeCardMetrics._pipelineLeadTimeHubRouteUrl;
            this.props.onCardClick(routeId, event);
            this._publishPipelineCardClickTelemetry(TelemetryConstants.feature_PipelineAnalyticsLeadTimeCard);
        }
    };

    private _publishPipelineCardClickTelemetry(featureName: string): void {
        const telemetryService = this.context.pageContext.getService<IPipelineAnalyticsTelemetryService>("IPipelineAnalyticsTelemetryService");
        telemetryService.publishEventWithSingleKey(featureName, TelemetryConstants.ClickCount, 1);
    }

    private _isReportOptedIn(currentDefinitionId: number): boolean {
        const pageContext = this.context.pageContext;
        const tfsPageData = getTFSPageData(pageContext);
        const projectId = tfsPageData!.project!.id;

        const settingsService = pageContext.getService<ISettingsService>("ISettingsService");
        const definitions = this._getRegistryValues<LeadTimeEnabledDefinitionIds[]>(PipelineLeadTimeCardMetrics._registryPath);

        // If definition id is present in the registry for this project return true.
        // Registry entry key value path is /Configuration/Settings/Host/Pipeline/leadtime/definitionIds
        if (definitions && definitions.length > 0) {
            const defForProject = definitions.find(definition => definition.projectId === projectId);
            if (defForProject && defForProject.definitionIds && defForProject.definitionIds.length > 0) {
                return defForProject.definitionIds.findIndex(id => id === currentDefinitionId) !== -1;
            }
        }

        return false;
    }

    private _getNumberOfDaysToCalculateLeadTime(){
        if(this._agoDays){
            return this._agoDays;
        }

        let registryValue = this._getRegistryValues<number>(PipelineLeadTimeCardMetrics._daysRegistryPath);
        if(registryValue){
            this._agoDays = registryValue;
        }
        else{
            this._agoDays = 90;
        }

        return this._agoDays;
    }

    private _getPercentileToCalculateLeadTime(){
        if(this._percentile){
            return this._percentile;
        }

        let registryValue = this._getRegistryValues<number>(PipelineLeadTimeCardMetrics._percentileRegistryPath);
        if(registryValue){
            this._percentile = registryValue;
        }
        else{
            this._percentile = 80;
        }

        return this._percentile;
    }

    private _getRegistryValues<T>(key: string) : T | undefined{
        const pageContext = this.context.pageContext;

        const settingsService = pageContext.getService<ISettingsService>("ISettingsService");
        return settingsService.getEntry<T>(
            key,
            SettingsUserScope.Host
        );
    }

    private _isLoadingComplete(){
        return this.state && !this.state.loading;
    }

    private _setLeadTimeMetricDataCache(definitionId: number, leadTimeMetrics: PipelineAnalytics.StageLeadTime) {
        this._cachedLeadTimeMetrics[definitionId] = leadTimeMetrics;
    }
 
    private _cachedLeadTimeMetrics:IDictionaryNumberTo<PipelineAnalytics.StageLeadTime> = {};
}

Components.add("pipelineLeadTimeCardMetrics", PipelineLeadTimeCardMetrics);
